import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, FreeMode, Navigation, Thumbs } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import VideoWrapper from "@/components/VideoWrapper";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

import classNames from "classnames";
import Image from "next/image";
import FavoriteButton from "./FavoriteButtonWide";
import { addWidthParam, addArchivedClass } from "@/lib/utils";

interface GallerySliderProps {
  images?: any;
  media?: Array<{
    type: 'image' | 'video';
    url: string;
    sort: number;
  }>;
  swiperSettings?: any;
  children: any;
  includeVideos?: boolean;
  isArchived?: boolean;
}

const breakPointDesktop = 1000;

const Icon: React.FC<{ direction: string }> = ({ direction }) => {
  const iconClass = classNames({
    "rotate-0": direction === "prev",
    "rotate-180": direction === "next",
  });

  return (
    <svg
      width="40"
      height="41"
      viewBox="0 0 40 41"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClass}
    >
      <rect
        width="40"
        height="40"
        rx="8"
        transform="matrix(-1 0 0 1 40 0.5)"
        fill="white"
      />
      <mask
        id="mask0_947_7488"
        maskUnits="userSpaceOnUse"
        x="8"
        y="8"
        width="24"
        height="25"
      >
        <rect
          width="24"
          height="24"
          transform="matrix(-1 0 0 1 32 8.5)"
          fill="#D9D9D9"
        />
      </mask>
      <g mask="url(#mask0_947_7488)">
        <path
          d="M22.6 26.5L24 25.1L19.4 20.5L24 15.9L22.6 14.5L16.6 20.5L22.6 26.5Z"
          fill="#2D2D32"
        />
      </g>
    </svg>
  );
};

const GallerySlider: React.FC<GallerySliderProps> = ({
  images,
  media,
  swiperSettings,
  children,
  includeVideos = false,
  isArchived = false,
}) => {
  const [openLightbox, setOpenLightbox] = useState(false);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});

  const sliderRef = useRef<any>(null);
  const thumbSliderRef = useRef<any>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  const [imageIndex, setImageIndex] = useState(0);

  // Use media array if available, otherwise fall back to images
  const mediaItems = useMemo(() => {
    if (media && media.length > 0) {
      return includeVideos 
        ? media 
        : media.filter(item => item.type === 'image');
    }
    // Fallback to old format
    const imageItems = images?.map((url: string) => ({
      type: 'image' as const,
      url,
      sort: 0,
    })) || [];
    return imageItems;
  }, [media, images, includeVideos]);

  const totalImages = mediaItems?.length || 0;

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef?.current?.swiper.slideTo(imageIndex);
      thumbSliderRef?.current?.swiper.slideTo(imageIndex);
    }
  }, [imageIndex]);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;

    setImageIndex((prev) => prev - 1);
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;

    setImageIndex((prev) => prev + 1);
  }, []);

  const containerClass =
    "-mb-2 md:mb-0 w-full h-[240px] relative md:h-[440px] md:min-h-[350px] group cursor-zoom md:max-h-[300px] xl:max-h-none";
  const swiperContainerClass = "w-full relative h-full";
  const swiperWrapperClass = "mb-16 md:mb-0";
  const swiperImageClass =
    "h-full w-full aspect-square rounded-lg object-cover";

  // Create slides for lightbox from media array
  const slides = useMemo(() => {
    if (media && media.length > 0) {
      const filteredMedia = includeVideos
        ? media
        : media.filter(item => item.type === 'image');

      return filteredMedia.map((item) => {
        if (item.type === 'video') {
          return {
            type: "video",
            sources: [
              {
                src: item.url,
                type: "video/mp4",
              },
            ],
          };
        }
        return {
          src: isArchived ? addArchivedClass(item.url, 1600) : addWidthParam(item.url, 1600),
        };
      });
    }
    // Fallback to old format
    return images?.map((image: any) => ({
      src: isArchived ? addArchivedClass(image, 1600) : addWidthParam(image, 1600),
    })) || [];
  }, [media, images, includeVideos, isArchived]);

  const memoizedLightbox = useMemo(() => {
    return (
      <Lightbox
        className={isArchived ? "archived-lightbox" : undefined}
        styles={{
          container: { backgroundColor: "rgba(255, 255, 255, .95)" },
          root: {
            "--yarl__color_button": "#2D2D32",
            "--yarl__color_button_disabled": "#dfdfdf",
            "--yarl__color_button_active": "#0a5599",
            "--yarl__thumbnails_thumbnail_border_color": "transparent",
            "--yarl__thumbnails_thumbnail_active_border_color": "#397ab6",
            "--yarl__button_filter": "none",
            "--yarl__thumbnails_container_background_color": "#0a5599",
            "--yarl__thumbnails_thumbnail_background": "transparent",
            "--yarl__color_backdrop": "none",
            "--yarl__slide_icon_loading_color": "#397ab6",
          },
        }}
        plugins={[Zoom, Thumbnails, Video]}
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={slides}
        index={imageIndex}
      />
    );
  }, [openLightbox, slides]);

  return (
    <>
      <style>
        {`:root {
            --swiper-pagination-color: var(--swiper-theme-color);
            --swiper-pagination-bullet-size: 12px;
            --swiper-pagination-bullet-width: 12px;
            --swiper-pagination-bullet-height: 12px;
            --swiper-pagination-bullet-horizontal-gap: 4px;
            --swiper-pagination-bullet-vertical-gap: 6px;
          }
          
          @media (min-width: ${breakPointDesktop}px) {
            .swiper-pagination {
              display: none !important;
            }
          }
          `}
      </style>
      <div className={containerClass}>
        {totalImages > 0 && imageIndex !== 0 && (
          <div
            className="hidden md:group-hover:block prev-arrow absolute z-10 -bottom-[30px] md:bottom-auto md:top-[50%] left-[51px] md:left-0 -translate-y-1/2 cursor-pointer [&>svg:hover]:-translate-x-[5px] duration-300 transition ml-3"
            onClick={handlePrev}
          >
            <Icon direction="prev" />
          </div>
        )}
        {totalImages > 0 && imageIndex < totalImages - 1 && (
          <div
            className="hidden md:group-hover:block next-arrow absolute z-10 -bottom-[30px] md:bottom-auto md:top-[50%] right-[51px] md:right-0 -translate-y-1/2 cursor-pointer [&>svg:hover]:translate-x-[5px] duration-300 transition mr-3"
            onClick={handleNext}
          >
            <Icon direction="next" />
          </div>
        )}
        <div className="swiper-slider group relative h-full">
          {memoizedLightbox}
          <Swiper
            slidesPerView={1}
            spaceBetween={30}
            navigation={false}
            pagination={{
              el: paginationRef.current,
              dynamicBullets: true,
              enabled: true,
            }}
            ref={sliderRef}
            className={swiperContainerClass}
            wrapperClass={swiperWrapperClass}
            modules={[Pagination]}
            onSlideChange={(swiper) => {
              setImageIndex(swiper.activeIndex);
              // Pause all videos when slide changes
              Object.values(videoRefs.current).forEach((video) => {
                if (video && !video.paused) {
                  video.pause();
                }
              });
            }}
          >
            {mediaItems &&
              mediaItems.map((item: any, index: any) => (
                <SwiperSlide
                  key={index}
                  onClick={() => {
                    setOpenLightbox(true);
                  }}
                >
                  {item.type === 'video' ? (
                    <div className="h-full">
                      <VideoWrapper 
                        ref={(el) => {
                          videoRefs.current[index] = el;
                        }}
                        src={item.url} 
                        className={swiperImageClass}
                      />
                    </div>
                  ) : (
                    <Image
                      fill
                      src={isArchived ? addArchivedClass(item.url, 1600) : addWidthParam(item.url, 1600)}
                      alt={`Slider image ${index}`}
                      className={swiperImageClass}
                      sizes="(max-width: 768px) 100vw, 800px"
                      priority={index === 0}
                      loading={index === 0 ? "eager" : "lazy"}
                      placeholder="blur"
                      blurDataURL="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNlNWU1ZTUiLz48L3N2Zz4="
                    />
                  )}
                </SwiperSlide>
              ))}
          </Swiper>
        </div>{" "}
      </div>
      {children}

      <div className="flex flex-col relative">
        {totalImages > 1 && (
          <div className="hidden md:flex justify-center">
            <Swiper
              ref={thumbSliderRef}
              spaceBetween={10}
              slidesPerView={8}
              freeMode={true}
              watchSlidesProgress={true}
              modules={[FreeMode, Navigation, Thumbs]}
              className="my-6 md:w-[450px]"
            >
              {mediaItems &&
                mediaItems.map((item: any, index: any) => {
                  const imageClass = classNames(
                    swiperImageClass,
                    "opacity-50 border-2 border-transparent cursor-pointer select-none",
                    {
                      "border-yellow !opacity-100": imageIndex === index,
                    }
                  );
                  return (
                    <SwiperSlide key={index} className="!h-[48px] !w-[48px]">
                      {item.type === 'video' ? (
                        <div 
                          className={classNames(imageClass, "relative")}
                          onClick={() => setImageIndex(index)}
                        >
                          <video 
                            src={item.url}
                            className="w-full h-full object-cover"
                          />
                          {/* Video play icon overlay */}
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="bg-blue/80 rounded-full p-1">
                              <svg
                                viewBox="0 0 24 24"
                                fill="white"
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Image
                          src={isArchived ? addArchivedClass(item.url, 700) : addWidthParam(item.url, 700)}
                          alt={`Slider image ${index}`}
                          className={imageClass}
                          onClick={() => setImageIndex(index)}
                          width={700}
                          height={700}
                        />
                      )}
                    </SwiperSlide>
                  );
                })}
            </Swiper>
          </div>
        )}

        {totalImages > 0 ? (
          <div className="arrows flex align-center justify-between mt-12 md:mt-0 md:static">
            {totalImages > 0 && imageIndex !== 0 ? (
              <div
                className="prev-arrow hidden md:block md:absolute z-10 md:bottom-auto md:top-[50%] md:left-0 md:-translate-y-1/2 cursor-pointer hover:-translate-x-[5px] duration-300 transition ml-4 opacity-0 group-hover:opacity-100"
                onClick={handlePrev}
              >
                <Icon direction="prev" />
              </div>
            ) : (
              <div className="block md:hidden"></div>
            )}

            <div
              className={`block w-50 swiper-pagination -mt-2 mb-2 md:mt-4`}
              ref={paginationRef}
            ></div>

            {totalImages > 0 && imageIndex < totalImages - 1 ? (
              <div
                className="next-arrow hidden md:block md:absolute z-10 md:bottom-auto md:top-[50%] md:right-0 md:-translate-y-1/2 cursor-pointer hover:translate-x-[5px] duration-300 transition mr-4 opacity-0 group-hover:opacity-100"
                onClick={handleNext}
              >
                <Icon direction="next" />
              </div>
            ) : (
              <div className="block md:hidden"></div>
            )}
          </div>
        ) : (
          <div className="block my-3"></div>
        )}
      </div>
    </>
  );
};

export default GallerySlider;
